import React, { useEffect, useMemo, useState } from 'react';

import { useQuery, useMutation, useQueryClient } from 'react-query';
import { deleteUser, loadJuries, loadNewestCourse } from '../utils/Network';
import { userAvatar } from '../utils';
import { useHistory, useLocation } from 'react-router-dom';
import CountryName from '../components/CountryName';
import CityName from '../components/CityName';
import SelectCourses from '../components/SelectCourses';

const JURY_PAGE_SIZE = 10;

function range(start, end) {
    return Array.apply(0, Array(end)).map((element, index) => index + start);
}

export default function JuriesContent() {
    const location = useLocation();
    const history = useHistory();
    const queryClient = useQueryClient();

    const [show_delete, setShowDelete] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const courseParam = searchParams.get('course');
    const currentPage = Number(searchParams.get('page')) || 1;

    const selectCourseValue =
        courseParam === null
            ? null
            : courseParam === 'all'
                ? 0
                : Number(courseParam);

    const needsDefaultCourse = courseParam === null;

    const { isSuccess: newestCourseReady, data: newestCourse } = useQuery(
        ['cms', 'juries', 'newestCourse'],
        loadNewestCourse,
        {
            enabled: needsDefaultCourse,
            staleTime: 60 * 1000,
        }
    );

    useEffect(() => {
        if (!needsDefaultCourse || !newestCourseReady) {
            return;
        }
        const params = new URLSearchParams(location.search);
        if (params.get('course')) {
            return;
        }
        if (newestCourse && newestCourse.id) {
            params.set('course', String(newestCourse.id));
        } else {
            params.set('course', 'all');
        }
        history.replace({
            pathname: '/cms/juries',
            search: params.toString(),
        });
    }, [needsDefaultCourse, newestCourseReady, newestCourse, history, location.search]);

    const apiCourse = courseParam === 'all' ? null : courseParam;

    const {
        data: juriesData,
        isLoading: juriesLoading,
        isFetching: juriesFetching,
        isError: juriesError,
    } = useQuery(
        ['cms', 'juries', 'list', currentPage, apiCourse ?? 'all'],
        () => loadJuries(currentPage, apiCourse, JURY_PAGE_SIZE),
        {
            enabled: courseParam !== null,
            keepPreviousData: true,
        }
    );

    const users = juriesData?.results ?? null;
    const count = juriesData?.count ?? null;
    const pages = useMemo(() => {
        const total = juriesData?.count ?? 0;
        if (total === 0) {
            return [];
        }
        const page_count =
            total % JURY_PAGE_SIZE === 0
                ? parseInt(total / JURY_PAGE_SIZE, 10)
                : parseInt(total / JURY_PAGE_SIZE, 10) + 1;
        return page_count > 0 ? range(1, page_count) : [];
    }, [juriesData?.count]);

    const deleteMutation = useMutation((userId) => deleteUser(userId), {
        onSettled: () => {
            queryClient.invalidateQueries(['cms', 'juries', 'list']);
        },
    });

    const onCourseSelect = (value) => {
        const params = new URLSearchParams(location.search);
        if (value === null || value === undefined || value === 0) {
            params.set('course', 'all');
        } else {
            params.set('course', String(value));
        }
        params.delete('page');
        history.push(`/cms/juries?${params.toString()}`);
    };

    const showDelete = (e) => {
        e.preventDefault();
        setShowDelete(!show_delete);
    };

    const handleDelete = (userId, e) => {
        deleteMutation.mutate(userId);
    };

    const deletingUserId = deleteMutation.isLoading ? deleteMutation.variables : null;

    const listBusy = needsDefaultCourse || (courseParam !== null && juriesLoading && !juriesData);

    return (
        <>
            <div className="content-wrapper">
                <section className="content-header" style={{ maxWidth: '1550px' }}>
                    <h3 className="box-title mt-5"></h3>
                    <ol className="breadcrumb">
                        <li><a href="/cms"><i className="fa fa-dashboard"></i> Home</a></li>
                        <li className="active">Juries</li>
                    </ol>
                </section>

                <section className="content">
                    <div className="container-fluid" style={{ maxWidth: '1400px' }}>
                    <div className="row">
                        <div className="col-md-12">
                            <div className="box box-primary">
                                <div className="box-header with-border">
                                    <h3 className="box-title pull-left">
                                        All juries
                                        {count != null && (
                                            <span style={{ fontSize: 11, marginLeft: '0.35em' }}>({count})</span>
                                        )}
                                    </h3>

                                    <a href="/cms/jury" title="Add New" className="btn btn-cm margin-r-5 pull-right"><i className="fa fa-plus"></i></a>
                                    <button type="button" title="Show Delete Button" className="btn btn-cm margin-r-5 pull-right" onClick={showDelete}>
                                        <i className="fa fa-trash"></i>
                                    </button>
                                    <div className="pull-right margin-l-10 " style={{ minWidth: '200px' }}>
                                        <SelectCourses
                                            value={selectCourseValue}
                                            width="220px"
                                            border="1"
                                            callback={onCourseSelect}
                                        />
                                    </div>
                                    <div className="clearfix" />
                                </div>
                                <div className="box-body no-padding">

                                    {juriesError && (
                                        <div className="padding-10 text-danger">Could not load juries. Please try again.</div>
                                    )}
                                    {listBusy && !juriesError && (
                                        <div className="padding-10 text-muted">Loading…</div>
                                    )}

                                    <table id="example1" className={`table table-striped${juriesFetching ? ' opacity-70' : ''}`}>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th className="hide-on-small">Gender</th>
                                                <th className="hide-on-small">POB</th>
                                                <th>Registered</th>
                                                <th style={{ width: 70 }}></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users && users.map((item) => (
                                                <tr key={item.id} className={deletingUserId === item.user ? 'loading' : ''}>
                                                    <td>
                                                        <div className="img-container ">
                                                            <a href={`/cms/jury/${item.id}`} >
                                                                <img src={userAvatar(item)} className="pull-left width-60 img-list img-circle margin-r-10" alt="" />
                                                            </a>
                                                        </div>

                                                        <a href={`/cms/jury/${item.id}`} className="link-color">
                                                            {item.first_name &&
                                                                <p className="text-capitalize mb-0 text-light">{item.first_name} {item.last_name}</p>
                                                            }
                                                            {!item.first_name &&
                                                                <p className="text-light">{item.email}</p>
                                                            }
                                                        </a>
                                                    </td>
                                                    <td className="hide-on-small">{item.gender === 0 ? 'Male' : item.gender === 1 ? 'Female' : 'Other'}</td>
                                                    <td className="hide-on-small"><CountryName id={item.country} /> <CityName id={item.city} /></td>
                                                    <td className="font-size-12">
                                                        {item.created_at && item.created_at.split(' ')[0]} <br />
                                                        {item.created_at && item.created_at.split(' ').length > 1 && item.created_at.split(' ')[1].split('.')[0]}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex" style={{ gap: '6px' }}>
                                                            {show_delete &&
                                                                <button type="button" onClick={(e) => { if (window.confirm('Are you sure you wish to delete this item?')) handleDelete(item.user, e) }} className="btn btn-cm btn-sm">
                                                                    <i className="fa fa-trash"></i>
                                                                </button>
                                                            }
                                                            <a href={`/cms/jury/${item.id}`} className="btn btn-cm btn-sm">
                                                                <i className="fa fa-eye"></i>
                                                            </a>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot>
                                            <tr>
                                                <th>Name</th>
                                                <th className="hide-on-small">Gender</th>
                                                <th className="hide-on-small">POB</th>
                                                <th>Registered</th>
                                                <th></th>
                                            </tr>
                                        </tfoot>
                                    </table>

                                </div>
                            </div>

                            {(() => {
                                const totalPages = pages.length;
                                const canGoPrev = totalPages > 0 && currentPage > 1;
                                const canGoNext = totalPages > 0 && currentPage < totalPages;
                                const pageHref = (num) => {
                                    const p = new URLSearchParams(location.search);
                                    p.set('page', String(num));
                                    return `/cms/juries?${p.toString()}`;
                                };
                                return (
                            <ul className="pagination cms-pagination m-0 float-right">
                                <li className={`page-item${!canGoPrev ? ' disabled' : ''}`}>
                                    <a
                                        className="page-link"
                                        href={canGoPrev ? pageHref(currentPage - 1) : '#'}
                                        onClick={!canGoPrev ? (e) => e.preventDefault() : undefined}
                                        aria-disabled={!canGoPrev}
                                    >
                                        «
                                    </a>
                                </li>
                                {pages.map((page_number) => (
                                    <li key={page_number} className="page-item">
                                        <a
                                            className={page_number === currentPage ? 'page-link active' : 'page-link'}
                                            href={pageHref(page_number)}
                                        >
                                            {page_number}
                                        </a>
                                    </li>
                                ))}
                                <li className={`page-item${!canGoNext ? ' disabled' : ''}`}>
                                    <a
                                        className="page-link"
                                        href={canGoNext ? pageHref(currentPage + 1) : '#'}
                                        onClick={!canGoNext ? (e) => e.preventDefault() : undefined}
                                        aria-disabled={!canGoNext}
                                    >
                                        »
                                    </a>
                                </li>
                            </ul>
                                );
                            })()}

                        </div>

                    </div>
                    </div>

                </section>



            </div>
        </>



    );
}
